import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const principles = [
  { icon: 'ri-flashlight-line', title: '更新优先', desc: '深夜档专区每日上新，保证读者第一时间追到最新剧情，不错过任何一话。' },
  { icon: 'ri-sword-line', title: '题材硬核', desc: '只收录真正带感的重口味、暗黑向作品，都市诡异、悬疑烧脑、血浆恐怖一网打尽。' },
  { icon: 'ri-palette-line', title: '画风大胆', desc: '从分镜到表现力层层把关，让每一部作品的暗黑美学与叙事冲击力都完整呈现。' },
  { icon: 'ri-shield-check-line', title: '体验纯净', desc: '界面简洁、加载流畅、无干扰弹窗，把最纯粹的追更体验留给你。' },
];

const testimonials = [
  { name: '夜猫子Lee', text: '深夜档更新是真的快，睡前刷一下新章基本都在，追更体验绝了。' },
  { name: '暗黑向阿夏', text: '重口味分区整理得很全，血浆恐怖和猎奇心理都有，画风也够大胆。' },
  { name: '雾都读者', text: '悬疑烧脑的作品很对我的胃口，剧情反转带感，收藏以后天天来。' },
];

const timeline = [
  { year: '2023', title: '创立初衷', desc: '一群暗黑美学爱好者，因找不到集中追更重口味剧情的去处而创立嘿嘿漫画网。' },
  { year: '2024', title: '深夜档上线', desc: '推出深夜档专区，实现每日更新机制，收获第一批忠实追更读者。' },
  { year: '2025', title: '三大专区成型', desc: '深夜档、重口味、暗黑向三大专区正式成型，题材体系日趋完善。' },
  { year: '2026', title: '持续深耕', desc: '坚持纯净阅读体验，专注硬核暗黑题材，陪伴每一位夜归人。' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于嘿嘿漫画网',
            description: '嘿嘿漫画网是专注于深夜档、重口味与暗黑向剧情漫画的在线阅读平台。',
            url: 'https://dailycartoon.github.io/about',
          }),
        }}
      />

      <Header />

      <main>
        <section className="relative overflow-hidden border-b border-background-200/60">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=dark%20manga%20artwork%20studio%20scene%20with%20sketchbooks%20ink%20bottles%20and%20crimson%20red%20warm%20lamp%20glow%20on%20a%20dark%20wooden%20desk%2C%20gritty%20illustration%20workspace%2C%20moody%20creative%20atmosphere&width=1600&height=500&seq=about-hero-2026&orientation=landscape"
              alt="嘿嘿漫画网关于我们暗黑创作场景"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/40"></div>
          </div>
          <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
            <nav aria-label="面包屑" className="mb-4 text-xs text-foreground-400">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="transition-colors hover:text-primary-400">首页</Link></li>
                <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
                <li className="text-foreground-300">关于我们</li>
              </ol>
            </nav>
            <h1 className="max-w-2xl font-heading text-3xl text-foreground-950 md:text-4xl">关于嘿嘿漫画网</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-300">
              我们是一群为暗黑美学而痴迷的人。从一部部悬疑烧脑的深夜剧情，到一帧帧画风大胆的重口味分镜，我们用最纯粹的立场，为喜欢硬核题材的你守住这片精神领地。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <div className="mb-8">
            <h2 className="font-heading text-2xl text-foreground-950">我们是谁</h2>
            <p className="mt-2 text-sm text-foreground-600">关于品牌定位与网站使命</p>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-background-200/60 bg-background-100 p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-compass-3-line text-xl" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg text-foreground-950">网站定位</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                嘿嘿漫画网是一个专注深夜档、重口味与暗黑向剧情的在线漫画平台。我们不做大而全，只做精而深——把都市诡异、悬疑烧脑、血浆恐怖、猎奇心理、暗黑人性与黑暗奇幻题材做到极致，让每一位喜欢硬核剧情的读者都能找到归属。
              </p>
            </div>
            <div className="rounded-2xl border border-background-200/60 bg-background-100 p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className="ri-eye-line text-xl" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg text-foreground-950">内容理念</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们相信，真正带感的故事不止于感官冲击，更在于对人性的凝视与追问。因此我们既保留重口味作品大胆的画风与张力，也坚持剧情的完整与深度，让暗黑不只是一种风格，更是一种思考。
              </p>
            </div>
          </div>
        </section>

        <section className="border-t border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
            <h2 className="mb-8 font-heading text-2xl text-foreground-950">内容原则</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p) => (
                <div key={p.title} className="rounded-xl border border-background-200/60 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-accent-400">
                    <i className={`${p.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 text-sm font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <h2 className="mb-8 font-heading text-2xl text-foreground-950">发展历程</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {timeline.map((t) => (
              <div key={t.year} className="relative rounded-xl border border-background-200/60 bg-background-100 p-5">
                <span className="font-heading text-lg text-primary-400">{t.year}</span>
                <h3 className="mt-2 text-sm font-bold text-foreground-950">{t.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
            <h2 className="mb-8 font-heading text-2xl text-foreground-950">读者口碑</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-xl border border-background-200/60 bg-background-50 p-5">
                  <div className="flex gap-1 text-accent-400">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm" aria-hidden="true"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-600">“{t.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100">
                      <i className="ri-user-3-line" aria-hidden="true"></i>
                    </span>
                    {t.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 text-center md:px-6">
          <h2 className="font-heading text-2xl text-foreground-950">加入我们，一起追更深夜档</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600">
            对重口味暗黑题材的热爱不需要理由。收藏嘿嘿漫画网，每天都能发现新的带感剧情。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/category/shenye" className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600">
              去追更 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
            <Link to="/contact" className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-6 py-2.5 text-sm text-foreground-950 transition-colors hover:border-primary-400/50">
              联系我们
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}